/* eslint-disable react/sort-comp, no-unused-vars */
import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { keyCode } from '@bingads-webui/keycode';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { Observable } from '@bingads-webui-universal/observable';

import {
  IWithArrowKeysOptions,
  IWithArrowKeysInjectedProps,
  IWithArrowKeysRuntimeFields,
} from './types';

export const WithArrowKeys = <P extends object>(
  ComponentToWrap: React.ComponentType<P & IWithArrowKeysInjectedProps>,
  staticOptions?: IWithArrowKeysOptions
): React.ComponentType<P & IWithArrowKeysOptions> => {
  const { interceptTabKey: interceptTabKeyFromStaticOptions = false } = staticOptions || {};
  class HoC extends React.PureComponent<P & IWithArrowKeysOptions> {
    static displayName = `WithArrowKeys(${getDisplayName(ComponentToWrap)})`;

    constructor(props: P & IWithArrowKeysOptions) {
      super(props);
      const hoc = this as this & IWithArrowKeysRuntimeFields;

      hoc.observable = new Observable<number>((observer) => {
        hoc.setData = data => observer.next(data);
      });

      this.onKeyDown = this.onKeyDown.bind(this);

      hoc.interceptTabKey = interceptTabKeyFromStaticOptions || props.interceptTabKey;
    }

    onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      const hoc = this as this & IWithArrowKeysRuntimeFields;

      if (e.keyCode === keyCode.ArrowLeft
        || e.keyCode === keyCode.ArrowRight
        || e.keyCode === keyCode.ArrowUp
        || e.keyCode === keyCode.ArrowDown
        || (e.keyCode === keyCode.Tab && hoc.interceptTabKey)) {
        e.preventDefault();
        e.stopPropagation();

        hoc.setData(e.keyCode);
      }
    }

    render() {
      const hoc = this as this & IWithArrowKeysRuntimeFields;

      return (
        <div
          role="presentation"
          onKeyDown={this.onKeyDown}
        >
          <ComponentToWrap observable={hoc.observable} {...(this.props as P)} />
        </div>
      );
    }
  }

  (HoC as typeof HoC & { defaultProps: Required<IWithArrowKeysOptions> }).defaultProps = {
    interceptTabKey: false,
  };

  return hoistNonReactStatics(HoC, ComponentToWrap);
};
